import React, { createContext, useCallback, useContext } from 'react';

import { useMutation, useSegment } from 'utils/hooks';

/**
 * @type {React.Context<{
 *  onDelete: () => void;
 *  isProcessing: boolean;
 * }>}
 */
const ProductReturnWindowDeleteContext = createContext();

export const useProductReturnWindowDelete = () => {
  const context = useContext(ProductReturnWindowDeleteContext);
  if (!context) {
    throw new Error(
      'useProductReturnWindowDelete must be used within a ProductReturnWindowDeleteProvider'
    );
  }
  return context;
};

/**
 * @param {object} props
 * @param {string} props.windowId
 * @param {keyof typeof import('../stubs').PRODUCT_WINDOW_TYPE} props.windowType
 * @param {() => void} props.onDeleted
 * @param {React.ReactNode} props.children
 */
export function ProductReturnWindowDeleteProvider({
  windowId = '',
  windowType = 'SKU',
  onDeleted = () => {},
  children = null
}) {
  const { track } = useSegment();
  const { isProcessing, mutate } = useMutation(
    windowType === 'SKU'
      ? 'configure.serviceWindow.exceptions.productSkus.root'
      : 'configure.serviceWindow.exceptions.productTypes.root',
    'DELETE',
    {
      routeParam: windowId,
      errorMessage: 'Failed to delete return window.',
      successMessage: 'Return window deleted successfully.',
      onValidate: () => !!windowId,
      onSuccess: () => {
        track('FTU Wizard Product Return Window Deleted', {
          window_id: windowId,
          window_type: windowType
        });
        onDeleted();
      }
    }
  );

  const onDelete = useCallback(() => mutate(), [mutate]);

  return (
    <ProductReturnWindowDeleteContext.Provider
      value={{
        onDelete,
        isProcessing
      }}
    >
      {children}
    </ProductReturnWindowDeleteContext.Provider>
  );
}
